import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, FileBarChart, Bot, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatUSD } from '../../lib/money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatMoneyOrText = (v) => (v === null || v === undefined || v === '' ? '—' : v);

/* BudgetAnalyzerTab — pick one of the uploaded budget docs, optionally
   add criteria, and let GPT-5.2 produce a structured ROI analysis (NPV,
   IRR, payback, risks, recommendation, narrative). The result is cached
   on the budget doc so admins can re-open it later. */
export default function BudgetAnalyzerTab({ adminToken }) {
  const [budgets, setBudgets] = useState([]);
  const [budgetId, setBudgetId] = useState('');
  const [extraCriteria, setExtraCriteria] = useState('');
  const [targetCustomer, setTargetCustomer] = useState('All-inclusive luxury travellers');
  const [region, setRegion] = useState('Caribbean (Jamaica)');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    if (!adminToken) return;
    setLoading(true);
    axios.get(`${API}/property-budgets`)
      .then(({ data }) => setBudgets(data || []))
      .finally(() => setLoading(false));
  }, [adminToken]);

  // Surface the cached analysis when admins re-pick a previously-analyzed doc.
  useEffect(() => {
    if (!budgetId) { setAnalysis(null); return; }
    const b = budgets.find((x) => x.id === budgetId);
    setAnalysis(b?.ai_analysis || null);
  }, [budgetId, budgets]);

  const runAnalysis = async () => {
    if (!budgetId) { toast.error('Pick a budget document first.'); return; }
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/admin/financial/analyze-doc`, {
        budget_id: budgetId,
        extra_criteria: extraCriteria.trim(),
        target_customer: targetCustomer.trim(),
        region: region.trim(),
      }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setAnalysis(data);
      // Update local copy so the cached analysis is visible without reload.
      setBudgets((bs) => bs.map((x) => x.id === budgetId ? { ...x, ai_analysis: data } : x));
      toast.success(`Analyzed · ${data.parse_status === 'parsed' ? 'document text extracted' : 'metadata-only run'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const exportPdf = async () => {
    if (!budgetId) return;
    setExportingPdf(true);
    try {
      const res = await axios.post(`${API}/admin/financial/analyze-doc/${budgetId}/export-pdf`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (analysis?.budget_title || 'analysis').replace(/[^A-Za-z0-9_\- ]/g, '').replace(/\s+/g, '_') || 'analysis';
      a.href = url;
      a.download = `ROI_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Board-deck PDF downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  if (!adminToken) {
    return <div className="p-8 text-center text-sm" style={{ color: '#7A8C8C' }}>Sign in as admin to use the AI analyzer.</div>;
  }

  return (
    <div data-testid="budget-analyzer-tab" className="space-y-4">
      {/* Inputs */}
      <div className="rounded-xl p-4 border" style={{ borderColor: '#E2DBC8', backgroundColor: 'white' }}>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold mb-3 inline-flex items-center gap-1.5" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
          <Bot className="w-3 h-3" /> AI ROI Analyst · GPT-5.2
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Budget document" required>
            {loading ? (
              <div className="text-xs" style={{ color: '#7A8C8C' }}><Loader2 className="w-3 h-3 inline animate-spin" /> Loading…</div>
            ) : budgets.length === 0 ? (
              <div className="text-xs" style={{ color: '#DC2626' }}>Upload a budget doc first via the Documents tab.</div>
            ) : (
              <select
                data-testid="analyzer-budget-pick"
                value={budgetId}
                onChange={(e) => setBudgetId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
                style={{ borderColor: '#D6D0C0', backgroundColor: 'white' }}
              >
                <option value="">Pick a budget…</option>
                {budgets.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title} {b.amount ? `· ${formatUSD(b.amount)}` : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Region">
            <input
              data-testid="analyzer-region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
              style={{ borderColor: '#D6D0C0' }}
            />
          </Field>
          <Field label="Target customer">
            <input
              data-testid="analyzer-customer"
              value={targetCustomer}
              onChange={(e) => setTargetCustomer(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
              style={{ borderColor: '#D6D0C0' }}
            />
          </Field>
          <Field label="Extra criteria (optional)">
            <textarea
              data-testid="analyzer-extra-criteria"
              value={extraCriteria}
              onChange={(e) => setExtraCriteria(e.target.value)}
              placeholder="e.g. assume 28% occupancy lift; emphasise sustainability ROI"
              rows={2}
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 resize-none"
              style={{ borderColor: '#D6D0C0' }}
            />
          </Field>
        </div>
        <div className="flex justify-end mt-3">
          <button
            data-testid="analyzer-run"
            onClick={runAnalysis}
            disabled={!budgetId || analyzing}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)', fontFamily: 'Manrope' }}
          >
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {analyzing ? 'Analyzing…' : 'Run analysis'}
          </button>
        </div>
      </div>

      {/* Output */}
      {analysis && (
        <div data-testid="analyzer-output" className="rounded-xl border bg-white" style={{ borderColor: '#E2DBC8' }}>
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E2DBC8' }}>
            <div className="inline-flex items-center gap-2">
              <FileBarChart className="w-4 h-4" style={{ color: '#003DA5' }} />
              <p className="text-sm font-bold" style={{ fontFamily: 'Manrope', color: '#1A2E2E' }}>
                {analysis.budget_title || 'Analysis'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{
                backgroundColor: analysis.parse_status === 'parsed' ? '#A3D4D4' : '#EAB30833',
                color: analysis.parse_status === 'parsed' ? '#0E1F2C' : '#92400E',
              }}>
                {analysis.parse_status === 'parsed' ? 'Doc text parsed' : 'Metadata only'}
              </span>
              <button
                data-testid="analyzer-export-pdf"
                onClick={exportPdf}
                disabled={exportingPdf}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold text-white disabled:opacity-40"
                style={{ backgroundColor: '#DC2626', fontFamily: 'Manrope' }}
                title="Hyatt-branded PDF for the board deck"
              >
                {exportingPdf ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                Board PDF
              </button>
            </div>
          </div>

          <div className="p-5 space-y-3">
            {/* Top metric strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Metric label="Investment" value={analysis.investment_total} accent="#003DA5" />
              <Metric label="Annual return" value={analysis.annual_return} accent="#15803D" />
              <Metric label="Payback" value={analysis.payback_months} accent="#1F4FBE" />
              <Metric label="IRR" value={analysis.irr_percent} accent="#8B5CF6" />
            </div>

            <Block title="Executive summary">
              <p className="text-sm leading-relaxed" style={{ color: '#1A2E2E' }}>{formatMoneyOrText(analysis.summary)}</p>
            </Block>
            <Block title="Strategic value">
              <p className="text-sm" style={{ color: '#1A2E2E' }}>{formatMoneyOrText(analysis.strategic_value)}</p>
            </Block>
            <Block title="Key assumptions">
              <pre className="text-xs whitespace-pre-wrap" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{formatMoneyOrText(analysis.key_assumptions)}</pre>
            </Block>
            <Block title="Risks">
              <p className="text-sm" style={{ color: '#1A2E2E' }}>{formatMoneyOrText(analysis.risk_notes)}</p>
            </Block>
            <Block title="Recommendation">
              <p className="text-sm font-bold" style={{ color: '#003DA5' }}>{formatMoneyOrText(analysis.recommendation)}</p>
            </Block>
            {analysis.full_text && (
              <Block title="Board-deck narrative">
                <pre className="text-xs whitespace-pre-wrap leading-relaxed" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{analysis.full_text}</pre>
              </Block>
            )}
            <p className="text-[10px] text-right italic" style={{ color: '#7A8C8C' }}>
              Generated {analysis.generated_at ? new Date(analysis.generated_at).toLocaleString() : 'just now'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#003DA5' }}>
        {label}{required && <span style={{ color: '#DC2626' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-1" style={{ color: '#003DA5' }}>{title}</p>
      {children}
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="rounded-lg px-3 py-2 border" style={{ borderColor: '#E2DBC8', backgroundColor: '#FAF8F2' }}>
      <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: accent }}>{label}</p>
      <p className="text-sm font-bold mt-0.5" style={{ color: '#1A2E2E' }}>{formatMoneyOrText(value)}</p>
    </div>
  );
}
